<script>
    import StatusCard from 'views/components/statusCard/StatusCard.svelte'
    import {mount, onDestroy, unmount} from 'svelte'
    import Lightbox from 'views/components/lightbox/Lightbox.svelte';
    import Util from 'util/util'
    import TextToSpeech from 'views/components/taskGroups/TextToSpeech.svelte';

    export let title = ''
    export let images = []
    export let columns = 1
    export let canZoom = true
    export let singleImageSize = null
    export let sourceId = null
    export let disableSpeech = false

    const minImageWidth = 200

    let lightbox
    let containerElement
    let containerWidth
    let resizeObserver
    let displayedColumns = columns
    updateContainerWidth()

    // Calc width of container element to set a CSS variable to set the best looking
    // maximum height for images.
    function updateContainerWidth() {
        window.requestAnimationFrame(() => {
            if (containerElement) {
                containerWidth = containerElement.clientWidth
                displayedColumns = Math.max(
                    1,
                    containerWidth < (minImageWidth * columns) ?
                        Math.floor(containerWidth / minImageWidth) : columns
                )
            }
        })
    }

    function addResizeObserver(el) {
        resizeObserver = new ResizeObserver(updateContainerWidth)
        resizeObserver.observe(el)
    }

    function openLightbox(image) {
        if (lightbox) {
            lightbox.openAtSlide = '/edu_files/open/' + image.url
            lightbox.isOpen = true
        } else {
            lightbox = mount(Lightbox, {
                target: document.body,
                props: {
                    slides: images.map(({url, caption}) => {
                        return {
                            src: '/edu_files/open/' + url,
                            caption,
                        }
                    }),
                    openAtSlide: '/edu_files/open/' + image.url,
                }
            })
        }
    }

    onDestroy(() => {
        resizeObserver.disconnect()
        if (lightbox) {
            unmount(lightbox)
        }
    })
</script>

<!-- eslint-disable-next-line svelte/no-at-html-tags -->
<header>{@html Util.renderContentSafely(title)}</header>
<div class="grid-container" bind:this={containerElement} use:addResizeObserver>
    <div
        class="grid"
        style:--number-of-columns={displayedColumns}
        style:--container-width={containerWidth + 'px'}
        style:--min-image-width={minImageWidth + 'px'}
    >
        {#each images as image, index (image.url)}
            <figure>
                {#if !disableSpeech && image.speechCaption}
                    <div class="speech-button">
                        <TextToSpeech
                            url="/sources/synthesize/{sourceId}.json?imageIndex={index}"
                            hasButtonStyling={true}
                        ></TextToSpeech>
                    </div>
                {/if}
                <!-- svelte-ignore a11y-no-static-element-interactions -->
                <svelte:element
                    this={canZoom ? 'button' : 'div'}
                    class="image-container"
                    class:can-zoom={canZoom}
                    on:click={() => canZoom ? openLightbox(image) : null}
                >
                    <img
                        src="/edu_files/open/{image.url}"
                        alt={image.caption}
                        class:large={images.length === 1 && singleImageSize === 'large'}
                        class:medium={images.length === 1 && singleImageSize === 'medium'}
                        class:small={images.length === 1 && singleImageSize === 'small'}
                        class:full-width={images.length === 1 && singleImageSize === null}
                    />
                </svelte:element>
                {#if image.caption || image.source}
                    <figcaption>
                        <!-- eslint-disable-next-line svelte/no-at-html-tags -->
                        {@html Util.renderContentSafely(Util.nl2br(image.caption))}
                        {#if image.source}
                            <cite data-source-prefix="{window.i18n.gettext('Source')}: ">{image.source}</cite>
                        {/if}
                    </figcaption>
                {/if}
            </figure>
        {:else}
            <StatusCard icon="warning" color="orange">{window.i18n.gettext('Can\'t find an image related to this source.')}</StatusCard>
        {/each}
    </div>
</div>

<style lang="scss">/*
* Scut, a collection of Sass utilities
* to ease and improve our implementations of common style-code patterns.
* v1.0.1
* Docs at http://davidtheclark.github.io/scut
*/
/* TODO implement tooltip arrow */
header {
  font-size: 26px;
  font-weight: 600;
  padding: 16px 0;
  line-height: 1.2;
  margin: 0 auto;
  overflow-wrap: break-word;
  hyphens: auto;
  text-align: center;
}
@media (max-width: 620px) {
  header {
    font-size: 20px;
    font-weight: 600;
    padding: 12px 0;
  }
}
header:empty {
  display: none;
}

.grid-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.grid {
  display: grid;
  grid-template-columns: repeat(var(--number-of-columns), minmax(0, 1fr));
  gap: max(8px, 20px - var(--number-of-columns) * 4px);
}

figure {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  overflow: clip;
  border-radius: 12px;
  outline: 1px solid hsl(40, 4%, 84%);
  transition: outline 0.2s;
  position: relative;
}
figure:focus-within {
  box-shadow: 0 0 10px 0 rgba(17.6%, 48.4%, 70.4%, 0.5);
}
figure:focus-within button:focus {
  outline: none;
}
figure:has(.image-container.can-zoom):hover {
  outline: 1px solid rgba(0, 0, 0, 0.25);
}

.speech-button {
  z-index: 1;
  position: absolute;
  top: 4px;
  left: 4px;
}

.image-container {
  position: relative;
  display: flex;
  height: 100%;
  width: 100%;
  background-color: hsl(40, 20%, 96%);
  transition: filter 0.2s;
}
.image-container.can-zoom {
  cursor: zoom-in;
}
.image-container.can-zoom:hover {
  filter: brightness(0.85) contrast(1.15);
}

img {
  display: block;
  min-width: var(--min-image-width);
  max-width: 100%;
  max-height: min(var(--container-width) / var(--number-of-columns), 300px);
  object-fit: scale-down;
  margin: auto;
}
img.small {
  max-width: 320px;
  max-height: unset;
}
img.medium {
  max-width: 560px;
  max-height: unset;
}
img.large {
  max-width: 800px;
  max-height: unset;
}
img.full-width {
  max-height: unset;
}

figcaption {
  font-size: 14px;
  text-align: center;
  text-wrap: balance;
  width: 100%;
  padding: 12px;
  background-color: #fff;
}
figcaption cite {
  font-size: 12px;
  color: rgba(18, 45, 64, 0.5);
  overflow-wrap: break-word;
  display: block;
}
figcaption cite::before {
  content: attr(data-source-prefix);
}</style>
